import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../Components/Navbar/Navbar';
import NewsItem from '../Components/SquareBlocks/NewsItem';
import Footer from '../Components/Footer/Footer';
import './News.css';

const News = () => {
  const { t } = useTranslation('news');
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedNews, setSelectedNews] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [useDirectFetch, setUseDirectFetch] = useState(false);

  const fetchData = async (currentPage) => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = useDirectFetch 
        ? `https://greentransitions.eu/api/get_news.php?page=${currentPage}&direct=1`
        : `https://greentransitions.eu/api/get_news.php?page=${currentPage}`;
      
      const response = await fetch(apiUrl, {
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get("content-type");
      const rawText = await response.text();
      
      if (debugMode) {
        console.log('Raw API response:', rawText);
        console.log('Content-Type:', contentType);
      }

      if (contentType && contentType.includes('text/html')) {
        console.error('Server returned HTML instead of JSON');
        
        if (debugMode) {
          const errorMatch = rawText.match(/<b>Fatal error<\/b>:(.*?)<br/i);
          if (errorMatch && errorMatch[1]) {
            console.error('PHP Error:', errorMatch[1].trim());
          }
        }
        
        throw new Error('Server returned HTML instead of JSON. Try toggling direct fetch mode with Alt+F.');
      }

      let cleanedText = rawText;
      if (rawText.trim() && (!rawText.startsWith('{') && !rawText.startsWith('[') || 
          !rawText.endsWith('}') && !rawText.endsWith(']'))) {
        
        const jsonStart = rawText.indexOf('[');
        const jsonEnd = rawText.lastIndexOf(']');
        
        if (jsonStart >= 0 && jsonEnd > jsonStart) {
          cleanedText = rawText.substring(jsonStart, jsonEnd + 1);
          console.warn('Extracted JSON from invalid response');
        } else {
          const objStart = rawText.indexOf('{');
          const objEnd = rawText.lastIndexOf('}');
          if (objStart >= 0 && objEnd > objStart) {
            cleanedText = rawText.substring(objStart, objEnd + 1);
            console.warn('Extracted JSON object from invalid response');
          }
        }
      }

      let responseData;
      try {
        responseData = JSON.parse(cleanedText);
      } catch (parseError) {
        console.error('JSON Parse error:', parseError);
        console.error('Response text:', rawText.substring(0, 500) + (rawText.length > 500 ? '...' : ''));
        throw new Error(`Invalid JSON response: ${parseError.message}`);
      }

      const data = Array.isArray(responseData) ? responseData : 
                  (responseData.data ? responseData.data : []);

      if (data.length === 0) {
        setHasMore(false);
      } else {
        setNews(prevNews => currentPage === 1 ? data : [...prevNews, ...data]);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setError(`${error.message} (Press Alt+D for debug mode, Alt+F to try a different fetch method)`);
      setHasMore(false);

      if (retryCount < 3) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchData(currentPage), 2000);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(page);
  }, [page]);

  useEffect(() => {
    const debounce = (func, delay) => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
      };
    };

    const handleScroll = debounce(() => {
      if (
        window.innerHeight + document.documentElement.scrollTop >= 
        document.documentElement.offsetHeight - 100 &&
        !loading &&
        hasMore
      ) {
        setPage(prevPage => prevPage + 1);
      }
    }, 200);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === 'd') {
        setDebugMode(prev => !prev);
        console.log('Debug mode:', !debugMode);
      }
      
      if (e.altKey && e.key === 'f') {
        setUseDirectFetch(prev => !prev);
        console.log('Direct fetch mode:', !useDirectFetch);
        setPage(1);
        setTimeout(() => fetchData(1), 100);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [debugMode, useDirectFetch]);

  const openNewsModal = (newsItem) => {
    console.log('News item clicked:', newsItem);
    setSelectedNews(newsItem);
    document.body.style.overflow = 'hidden';
  };

  const closeNewsModal = () => {
    setSelectedNews(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="news">
      <Navbar />
      <div className="project-content">
        <h1 className="page-title">{t('title')}</h1>
        <div className="title-news-spacer"></div>
        
        {debugMode && (
          <div className="debug-info" style={{background: '#f8f9fa', padding: '10px', marginBottom: '15px', borderRadius: '5px'}}>
            Debug: {useDirectFetch ? 'Using direct fetch' : 'Using standard fetch'}
          </div>
        )}
        
        {error && (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button 
              onClick={() => {
                setUseDirectFetch(prev => !prev);
                setPage(1);
                setRetryCount(0);
                setTimeout(() => fetchData(1), 100);
              }}
              style={{marginTop: '10px', padding: '5px 10px'}}
            >
              Try {useDirectFetch ? 'Standard' : 'Direct'} Fetch
            </button>
          </div>
        )}
        
        <div className="news-container">
          {news.length > 0 ? (
            news.map((item, index) => (
              <div 
                key={`${item.id || index}`}
                onClick={() => openNewsModal(item)}
                className="news-item-wrapper"
              >
                <NewsItem {...item} />
              </div>
            ))
          ) : !loading && !error && (
            <p className="no-news-message">{t('noNews')}</p>
          )}
        </div>
        {loading && <p className="loading-text">{t('loading')}</p>}
        {!hasMore && news.length > 0 && !loading && (
          <p className="no-more-news">{t('noMore')}</p>
        )}
        
        {(!loading && news.length === 0) && <div className="news-page-spacer"></div>}
      </div>

      {selectedNews && (
        <div className="news-modal-overlay" onClick={closeNewsModal}>
          <div className="news-modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeNewsModal}>×</button>
            <h2 className="modal-title">{selectedNews.title}</h2>
            <div className="modal-date">{selectedNews.date}</div>
            <div className="modal-content-body">
              {(() => {
                const content = selectedNews.news_text || 
                              selectedNews.content || 
                              selectedNews.main_text ||
                              (selectedNews.text ? selectedNews.text : '') ||
                              'No detailed content available';
                
                return content.split(/\n\n|\r\n\r\n/).map((paragraph, index) => {
                  const cleanParagraph = paragraph
                    .replace(/<\/?[^>]+(>|$)/g, '')
                    .trim();
                  
                  return cleanParagraph ? (
                    <p key={index} className="modal-paragraph">
                      {cleanParagraph}
                    </p>
                  ) : null;
                }).filter(Boolean);
              })()}
            </div>
            {selectedNews.link && (
              <a href={selectedNews.link} className="modal-read-more" target="_blank" rel="noopener noreferrer">
                Read full article
              </a>
            )}
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default News;